import { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { CollectionsViewPageHeader } from "@/pages/collections-view/components/CollectionsViewPageHeader";
import { CollectionsViewPageList } from "@/pages/collections-view/components/CollectionsViewPageList";
import { DeleteCollectionsModal } from "@/components/modal/delete-collections/delete-collections-modal";
import { DeleteSharedNotesModal } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal";
import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { RemoveNotesFromSharedCollectionModal } from "@/components/modal/remove-notes-from-shared-collection/remove-notes-from-shared-collection-modal";
import { MakeCollectionPrivateModal } from "@/components/modal/make-collection-private/make-collection-private-modal";
import { MdsPanelContainer } from "@/design-system/components/panel";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { Appear } from "@/components/appear";
import styled from "@emotion/styled";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { EntityNotFoundType, NotFoundPage } from "@/pages/not-found/NotFoundPage";

interface CollectionsViewPageProps {
  isSidePanel?: boolean;
}

export const CollectionsViewPage: FC<CollectionsViewPageProps> = observer(
  ({ isSidePanel = false }) => {
    const pageViewStore = useTabPageStore("collectionsViewPage");
    pageViewStore.useEffects();

    /**
     * Track views of the collection.
     */
    useEffect(() => {
      if (!pageViewStore.collection) return;
      trackEvent(TrackedEvent.CollectionView, {
        collection_id: pageViewStore.collection.id,
        collection_primary_label: pageViewStore.collection.label,
        is_in_side_panel: isSidePanel,
      });
    }, [pageViewStore.collection, isSidePanel]);

    if (pageViewStore.showNotFound) {
      return <NotFoundPage entityType={EntityNotFoundType.Collection} />;
    }

    return (
      <StyledAppear visible={pageViewStore.isSubscriptionInitialized}>
        <MakeCollectionPrivateModal store={pageViewStore.makeCollectionPrivateModal} />
        <ShareSheetModal store={pageViewStore.shareSheetModal} />
        <DeleteCollectionsModal state={pageViewStore.deleteCollectionsModal} />
        <DeleteSharedNotesModal store={pageViewStore.deleteSharedNotesModal} />
        <RemoveNotesFromSharedCollectionModal
          store={pageViewStore.removeNotesFromSharedCollectionModal}
        />
        <CollectionsViewPageHeader />
        <MdsPanelContainer>
          <CollectionsViewPageList />
        </MdsPanelContainer>
      </StyledAppear>
    );
  }
);

const StyledAppear = styled(Appear)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden",
  height: "100%",
});
